import React, { useState } from 'react';
import { FiChevronsLeft, FiChevronsRight } from 'react-icons/fi';
import { GoPrimitiveDot } from 'react-icons/go';
import { Translate } from 'react-localize-redux';
import Swipeable from 'react-swipeable';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import useInterval from '@use-it/interval';
import styled, { css } from 'styled-components';

import ImageLightBox from '../../components/image-light-box';
import FEATURE_1 from './images/feature_1.png';
import FEATURE_2 from './images/feature_2.png';
import FEATURE_3 from './images/feature_3.png';
import Section from './section';

const images = [FEATURE_1, FEATURE_2, FEATURE_3];

const MobileContainer = styled(Swipeable)`
  @media (min-width: 768px) {
    display: none;
  }
`;

const DotContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const Dot = styled(GoPrimitiveDot)`
  color: rgba(255, 255, 255, 0.5);
  margin: 0 0.5em;

  ${props =>
    props.active
      ? css`
          color: #fff;
        `
      : css`
          cursor: pointer;
        `}
`;

const TransitionContainer = styled.div`
  &.enter {
    opacity: 0;
  }

  &.enter-active {
    opacity: 1;
    transition: 350ms ease;
  }

  &.exit {
    opacity: 0;
    transition: 350ms ease;
  }
`;

const TabletContainer = styled.div`
  display: none;

  @media (min-width: 768px) {
    display: grid;
    grid-gap: 1em;
    grid-template-columns: 1fr 1fr 1fr;
  }
`;

const ImageContainer = styled.div`
  margin-top: 0.8em;
  position: relative;
`;

const Image = styled.img`
  width: 100%;
`;

const TabletImage = styled(Image)`
  cursor: pointer;
`;

const TextContainer = styled.div`
  background: #111111;
  color: #fff;
  line-height: 1.75;
  margin-top: 1.5em;
  padding: 1.5em;

  @media (min-width: 768px) {
    font-size: 0.75em;
  }
`;

const Text = styled.p`
  margin: 0;
`;

const Button = styled.div`
  cursor: pointer;
  font-size: 2.5em;
  background: ${props => props.theme.primary};
  color: white;
  position: absolute;
  width: 1em;
  height: 1em;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Next = styled(Button)`
  right: -0.5em;
  top: 50%;
  transform: translateY(-50%);
`;

const Previous = styled(Button)`
  left: -0.5em;
  top: 50%;
  transform: translateY(-50%);
`;

const Features = () => {
  const [autoplay, setAutoplay] = useState(true);
  const [index, setIndex] = useState(0);
  const incrementIndex = () => {
    setAutoplay(false);
    setIndex((index + 1) % images.length);
  };
  const decrementIndex = () => {
    setAutoplay(false);
    setIndex((index - 1 + images.length) % images.length);
  };

  useInterval(() => {
    if (autoplay) {
      setIndex((index + 1) % images.length);
    }
  }, 7500);

  const [openImage, setOpenImage] = useState();

  return (
    <Section
      heading="FEATURES"
      headingPosition="top"
      subHeading={<Translate id="psyLandingPage.features.subtitle" />}
    >
      <MobileContainer
        onSwipedLeft={incrementIndex}
        onSwipedRight={decrementIndex}
      >
        <DotContainer>
          <Dot
            active={Number(index === 0)}
            onClick={() => setIndex(0)}
            size="1.5em"
          />
          <Dot
            active={Number(index === 1)}
            onClick={() => setIndex(1)}
            size="1.5em"
          />
          <Dot
            active={Number(index === 2)}
            onClick={() => setIndex(2)}
            size="1.5em"
          />
        </DotContainer>
        <SwitchTransition>
          <CSSTransition key={index} timeout={350}>
            <TransitionContainer>
              <ImageContainer>
                <Image src={images[index]} />
                <Previous onClick={decrementIndex}>
                  <FiChevronsLeft />
                </Previous>
                <Next onClick={incrementIndex}>
                  <FiChevronsRight />
                </Next>
              </ImageContainer>
              <TextContainer>
                <Text>
                  <Translate
                    id={`psyLandingPage.features.main.${index + 1}.1`}
                    options={{ renderInnerHtml: true }}
                  />
                </Text>
                <Text>
                  <Translate
                    id={`psyLandingPage.features.main.${index + 1}.2`}
                  />
                </Text>
              </TextContainer>
            </TransitionContainer>
          </CSSTransition>
        </SwitchTransition>
      </MobileContainer>
      <TabletContainer>
        {images.map((image, i) => (
          <div key={i}>
            <TabletImage onClick={() => setOpenImage(image)} src={image} />
            <TextContainer>
              <Text>
                <Translate
                  id={`psyLandingPage.features.main.${i + 1}.1`}
                  options={{ renderInnerHtml: true }}
                />
              </Text>
              <Text>
                <Translate id={`psyLandingPage.features.main.${i + 1}.2`} />
              </Text>
            </TextContainer>
          </div>
        ))}
        {openImage && (
          <ImageLightBox onClose={() => setOpenImage(null)} src={openImage} />
        )}
      </TabletContainer>
    </Section>
  );
};

export default Features;
